import React, { useMemo, lazy, Suspense } from "react";
import { createBrowserRouter, RouterProvider, useParams } from "react-router-dom";
import Login from "../components/Authentication/Login";
import ErrorPage from "./Pages/ErrorPage";
import MainLayout from "./Layout/MainLayout";
import Customizations from "./Pages/Customization/Customizations";
import MyProfilePage from "./Pages/Profile/MyProfilePage";
import LinkPage from "./Pages/Links/LinkPage";
import Users from "./Pages/Admin/Users/AdminUsersPage";
import Organizations from "./Pages/Admin/Organisation/AdminOrgPage";
import AdminCustomisationLogPage from "./Pages/Admin/Customisation/AdminCustomisationLogPage";
import RecepientsPage from "./Pages/Recipients/RecepientsPage";
import SupportPage from "./Pages/Support/SupportPage";
import RecipientGlancePage from "./Pages/Recipients/RecipientGlancePage";
import AddBotPage from "./Pages/Admin/PotentialBots/AddBotsPage";
import AddSubscriptionPage from "./Pages/Admin/Subscription/AddSubscriptionPage";
import AdminSubscriptionsPage from "./Pages/Admin/Subscription/AdminSubscriptionsPage";
import AdminPotentialBotsPage from "./Pages/Admin/PotentialBots/AdminPotentialBotsPage";
import AdminModelRoutingPage from "./Pages/Admin/ModelRouting/AdminModelRoutingPage";
import AdminApiUsagePage from "./Pages/Admin/ApiUsage/AdminApiUsagePage";
import AdminImpersonationLogPage from "./Pages/Admin/ImpersonationLog/AdminImpersonationLogPage";
import ErrorLaserLINK from "./Pages/Links/ErrorLaserLINK";
import { StatsProvider } from "./Pages/Stats/StatsContext";
import CreateNewKdf from "./Pages/Kdf/KdfListing/CreateNewKdf.js";
import KdfSettings from "./Pages/Kdf/KdfListing/KdfSettings.js";
import KdfListPage from "./Pages/Kdf/KdfListing/KdfListPage.js";
import AdminMasterDomainsPage from "./Pages/Admin/Domains/AdminMasterDomainsPage";
import AdminOrganizationDomainPage from "./Pages/Admin/Domains/AdminOrganizationDomainPage";
import Tl404Page from "./Pages/Tl404Page";
import LoadingScreen from "./Elements/LoadingScreen/LoadingScreen";

const EditKdfPage = lazy(() => import("./Pages/Kdf/EditKdf/EditKdfPage"));
const StatsView = lazy(() => import("./Pages/Stats/StatsView"));
const JourneyPage = lazy(() => import("./Pages/Journey/JourneyPage"));
const PersonalInsightsPage = lazy(() => import("./Pages/PersonalInsights/PersonalInsightsPage"));
const AdminHostedDocumentsPage = lazy(() => import("./Pages/Admin/HostedDocuments/AdminHostedDocumentsPage"));
const AdminLegalDocumentsPage = lazy(() => import("./Pages/Admin/LegalDocuments/AdminLegalDocumentsPage"));
const RriPage = lazy(() => import("./Pages/Rri/RriPage"));

const StatsRouteElement = ({ header, props }) => {
  const { id } = useParams();
  return (
    <StatsProvider key={id}>
      <MainLayout header={header} props={props}>
        <StatsView {...props} />
      </MainLayout>
    </StatsProvider>
  );
};

const MainBody = (props) => {
  if (props?.page === "tl_404") {
    return <Tl404Page message={props?.message} />;
  }

  // Memoize the router so it is created once and not recreated on every render.
  // Recreating the router causes all route components to unmount/remount,
  // which triggers duplicate API calls and resets component state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const appRouter = useMemo(() => createBrowserRouter([
    {
      path: "/",
      element: <Login status="login" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/users/login",
      element: <Login status="login" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/users/confirmation/new",
      element: <Login status="confirmation" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/users/confirmation",
      element: <Login status={props?.confirmationToken ? "confirmAccount" : "confirmation"} props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/confirm",
      element: <Login status="confirmAccount" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/users/unlock/new",
      element: <Login status="unlock" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/users/unlock",
      element: <Login status="unlock" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "users/password/new",
      element: <Login status="forgotPassword" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "users/password",
      element: <Login status="forgotPassword" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/signup",
      element: <Login status="signup" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/kdfs",
      element: (
        <MainLayout header="KDFs" props={props}>
          <KdfListPage props={props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/kdfs/token/:token",
      element: (
        <MainLayout header="KDFs" props={props}>
          <KdfListPage props={props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/support",
      element: (
        <MainLayout header="Support" props={props}>
          <SupportPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/customizations",
      element: (
        <MainLayout header="" props={props}>
          <Customizations {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/profile",
      element: (
        <MainLayout header="" props={props}>
          <MyProfilePage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/kdfs/new",
      element: (
        <MainLayout header="Create New" props={props}>
          <CreateNewKdf props={props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/kdfs/:id",
      element: (
        <MainLayout header="KDF Settings" props={props}>
          <KdfSettings {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },

    {
      path: "/editor/:id",
      element: <EditKdfPage {...props} editortype="editor" />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/editor/:id/:locale",
      element: <EditKdfPage {...props} editortype="editor" />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/links",
      element: (
        <MainLayout header="Your LaserLINX" props={props}>
          <LinkPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/links/doc/:token",
      element: (
        <MainLayout header="Your LaserLINX" props={props}>
          <LinkPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/links/tl/:token",
      element: (
        <MainLayout header="Your LaserLINX" props={props}>
          <LinkPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/users",
      element: (
        <MainLayout header="" props={props}>
          <Users {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/orgs",
      element: (
        <MainLayout header="" props={props}>
          <Organizations {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/subscriptions",
      element: (
        <MainLayout header="" props={props}>
          <AdminSubscriptionsPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/potential_bots",
      element: (
        <MainLayout header="" props={props}>
          <AdminPotentialBotsPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/model_routing",
      element: (
        <MainLayout header="" props={props}>
          <AdminModelRoutingPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/api_usage",
      element: (
        <MainLayout header="" props={props}>
          <AdminApiUsagePage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/impersonation_logs",
      element: (
        <MainLayout header="" props={props}>
          <AdminImpersonationLogPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/potential_bots/new",
      element: (
        <MainLayout header="" props={props}>
          <AddBotPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/subscriptions/new",
      element: (
        <MainLayout header="" props={props}>
          <AddSubscriptionPage {...props} type='add' />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/subscriptions/:id/edit",
      element: (
        <MainLayout header="" props={props}>
          <AddSubscriptionPage {...props} type="edit" />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/customization_log",
      element: (
        <MainLayout header="" props={props}>
          <AdminCustomisationLogPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/master_domains",
      element: (
        <MainLayout header="" props={props}>
          <AdminMasterDomainsPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: `/admin/organization_domains`,
      element: (
          <MainLayout header="" props={props}>
              <AdminOrganizationDomainPage {...props} />
          </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/recipients",
      element: (
        <MainLayout header="Recipients" props={props}>
          <RecepientsPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/recipients/:id",
      element: (
        <MainLayout header="Recipient - At a Glance" props={props}>
          <RecipientGlancePage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/viewer/:id/:lang/dev",
      element: <EditKdfPage {...props} editortype="preview" />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/viewer/:id/:lang",
      element: <EditKdfPage {...props} editortype="publish" />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/:id/:lang",
      element:
          props?.page === "error"
              ? <ErrorLaserLINK errorText={props?.error_text} />
              : <EditKdfPage {...props} editortype="laserlink" />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/tl-stats/:id",
      element: <StatsRouteElement header="LaserLINK Statistics" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/kdf-stats/:id",
      element: <StatsRouteElement header="KDF Statistics" props={props} />,
      errorElement: <ErrorPage />,
    },
    {
      path: "/personal-insights",
      element: (
        <MainLayout header="Personal Insights" props={props}>
          <PersonalInsightsPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/hosted_documents",
      element: (
        <MainLayout header="" props={props}>
          <AdminHostedDocumentsPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/admin/legal_documents",
      element: (
        <MainLayout header="" props={props}>
          <AdminLegalDocumentsPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/kdf-journey/:token/:receiverId",
      element: (
        <MainLayout header="Recipient Journey" props={props}>
          <JourneyPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: "/kdf-rri/:token/:receiverId",
      element: (
        <MainLayout header="Recipient Response Intelligence" props={props}>
          <RriPage {...props} />
        </MainLayout>
      ),
      errorElement: <ErrorPage />,
    }
  ],  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }
    }), []); // Empty deps: props come from React.memo parent and don't change
  return (
    <Suspense fallback={<LoadingScreen />}>
      <RouterProvider router={appRouter} />
    </Suspense>
  );
};
export default MainBody;
